import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ThreeDots } from "react-loader-spinner";
import { FaAngleLeft, FaExclamationTriangle } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";
import { dateToUnixTimestamp, formatTimestamp, formatDateStringNoDay } from "../../../../util_functions/helpers";
import config from "../../../../config/config";
import axios from "axios";

const initialMessage = {
  client: "",
  clientId: "",
  worker: "",
  workerId: "",
  latestMessage: "",
  isAgreementSigned: false,
  isMeetAndGreetSet: false,
  isMeetAndGreetConfirmed: false,
  meetAndGreetSetTimestamp: null,
  fullMessages: [],
  timestamp: dateToUnixTimestamp(new Date()),
};

export default function QuickApplyForm({ user, clientId, clientName, selectedJob, onBack, onFinish }) {
  const workerId = user?.id;
  const jobId = selectedJob?.id;
  const [message, setMessage] = useState("");
  const [messageData, setMessageData] = useState(initialMessage);
  const [messageSent, setMessageSent] = useState(false);
  const [hasContacted, setHasContacted] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [appliedTimestamp, setAppliedTimestamp] = useState(null);
  const navigate = useNavigate();

  const baseURL = config.API_BASE_URL;
  const isSmallMobile = useMediaQuery({ query: "(max-width: 480px)" });

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const formats = [
    "font", "size", "bold", "italic", "underline", "strike",
    "list", "bullet", "link", "image", "color",
  ];

  useEffect(() => {
    if (!workerId || !clientId || !jobId) return;

    const checkApplication = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/v1/messages/thread-job`, {
          params: { workerId, clientId, jobId },
        });
        console.log("thread check →", data);

        setHasContacted(data.hasApplied);
        setThreadId(data.threadId || null);

        if (data.hasApplied) {
          const listResp = await axios.get(`${baseURL}/v1/messages`, {
            params: { workerId, clientId, jobId },
          });
          const list = listResp.data;
          console.log("[QuickApplyForm] existing messages →", list);
          if (Array.isArray(list) && list.length > 0) {
            const thread = list[0].threadId || list[0]._id;
            setThreadId(thread);
          }
        }
      } catch (err) {
        console.error("[PSInbox] error checking application", err);
      }
    };

    checkApplication();
  }, [workerId, clientId, jobId]);

  useEffect(() => {
    if (!selectedJob || !workerId) return;

    const myInfo = (selectedJob.applicantInfos || []).find(
      (info) => info.applicantId === workerId
    );

    if (myInfo) {
      setAppliedTimestamp(myInfo.applicationTimestamp);
    }
  }, [selectedJob, workerId]);


  useEffect(() => {
    if (selectedJob) {
      const rawTitle = selectedJob.jobCraftDetails.jobTitle || "";
      const cleanTitle = rawTitle.trim().replace(/\.$/, "");
      setMessage(
        `<p>Hello <strong>${clientName.split(" ")[0]}</strong>,</p>` +
        `<p>&nbsp;</p>` +
        `<p>I am writing to apply for the position listed above.</p>` +
        `<p>&nbsp;</p>`
      );
    }
  }, [selectedJob, clientName, user]);

  const applyForJob = async () => {
    try {
      await axios.post(
        `${baseURL}/v1/jobs/apply/${jobId}/${workerId}`
      );
    } catch (error) {
      console.error(error);
    }
  };

  const postMessage = async (payload) => {
    try {
      const resp = await axios.post(`${baseURL}/v1/messages`, payload);
      if (resp.status === 200 || resp.status === 201) {
        setMessageSent(true);
        await applyForJob();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = () => {
    if (hasContacted) return;
    const profileUrl = `${baseURL}/v1/worker-users/${workerId}`;
    const profileLinkButton =
      `<a href="${profileUrl}" ` +
      `class="view-profile-btn" ` +
      `target="_blank" rel="noopener">` +
      `View my profile` +
      `</a></p>`;
    const finalMessage = message + profileLinkButton;
    const now = dateToUnixTimestamp(new Date());
    const newMsg = {
      text: finalMessage,
      chatMessageTimeStamp: now,
      sender: "worker",
      receiver: "client",
      jobId: jobId,
      read: false,
    };

    const payload = {
      ...messageData,
      client: clientName,
      clientId,
      worker: `${user.firstName} ${user.lastName}`,
      workerId: workerId,
      latestMessage: finalMessage,
      appliedTimestamp: now,
      fullMessages: [...messageData.fullMessages, newMsg],
    };

    setMessageData(payload);
    postMessage(payload);
  };

  const handleBack = () => onBack();
  const charCount = message.length;

  return (
    <div className="quick-apply-form">
      <div className="content-button-container">
      <div className="quick-apply-form-content">
        {messageSent && (
          <div className="modal-success-message">
            <h3>
               <FaCircleCheck className="success-application-icon"/>
              Application Submitted</h3>
            <p>
              Success! Your application was sent to <strong>{clientName.split(" ")[0]}</strong>. A confirmation is in your inbox.
            </p>
            <button onClick={() => onFinish(threadId)}>Go to Inbox</button>
          </div>
        )}

        {!messageSent && hasContacted && (() => {

          return (
            <div className="modal-warning-message">
              <h3 className="alert-header">
                <FaExclamationTriangle className="alert-icon" />
                Reminder</h3>
              <p>
                Your application was sent on {isSmallMobile && <br />} {formatDateStringNoDay(appliedTimestamp)}. You can continue the conversation in your inbox thread.
              </p>

              <div className="job-applied-buttons">
                <button
                  className="job-applied-button"
                  onClick={() => onFinish(threadId)}
                >
                  Go to Inbox
                </button>
                <button
                  className="job-applied-button"
                  onClick={() =>
                    navigate("/account/ps-dashboard/my-applications")
                  }
                >
                  View Applications
                </button>
              </div>
            </div>
          );
        })()}


        {hasContacted === null && !messageSent && <div className="loading-spinner">
          <ThreeDots
            visible={true}
            height="80"
            width="40"
            color="#4fa94d"
            radius="9"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
        </div>}

        {!messageSent && hasContacted === false && (
          <div>
            <div className="apply-header">
              <FaAngleLeft
                className="quick-apply-back"
                onClick={handleBack}
              />
              <h4>{selectedJob.jobCraftDetails.jobTitle}</h4>
            </div>
            <div className="application-description">
              <p>
                Please describe how your skills, experience, and availability meet the requirements for this job.
              </p>
            </div>
            <div className="textarea-container">
              <ReactQuill
                theme="snow"
                value={message}
                onChange={setMessage}
                modules={modules}
                formats={formats}
                placeholder="Enter your message here…"
                className="react-quill-custom"
              />
              <div className="charCount-labels">
                <span>
                  {charCount < 25 ? `Minimum characters: ${25 - charCount}` : ""}
                </span>
                <span>{`${charCount}/4000`}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {!messageSent && hasContacted === false && (
        <div className="quick-apply-submit">
          <button onClick={handleSubmit} disabled={charCount < 25}>
            Submit
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
